import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowPathIcon, UserCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { ONBOARDING_TASKS_CONFIG, getTaskLink } from '../config/onboardingTasks.js';
import { shouldShowKeyMetricsNav } from '../hooks/useKeyMetricAmounts.js';

const HIDDEN_PATHS = [
  '/onboarding',
  '/onboarding-v2',
  '/onboarding-payment',
  '/sign-in',
  '/sign-up',
  '/workspace-selection',
];

const ACCOUNT_STEP = {
  name: 'Set up your account',
  alwaysComplete: true,
  icon: UserCircleIcon,
};

const STEP_LABELS = {
  'Complete Profile': 'Build your company profile',
  'Upload or Integrate Expenses': 'Connect your expenses',
  'Redeem Savings': 'Cash in your software savings',
  'Add to My Grants': 'Add grants worth winning',
  'Try 3 AI Automations': 'Switch on 3 AI automations',
  'Try 3 Revenue Upgrades': 'Stack 3 revenue upgrades',
  'Ideal Client Intro': 'Meet an ideal client',
  'Refer & Earn More': 'Refer a founder and earn',
};

function checklistSteps() {
  return [
    ACCOUNT_STEP,
    ...ONBOARDING_TASKS_CONFIG.map((task) => ({
      ...task,
      name: STEP_LABELS[task.name] || task.name,
    })),
  ];
}

function companySearch(company, location) {
  const search = location?.search || '';
  if (search.includes('website=')) return search.startsWith('?') ? search : `?${search}`;
  const domain = company?.fields?.Domain;
  if (!domain) return '';
  const name = company?.fields?.Name || 'Company';
  return `?website=${encodeURIComponent(domain)}&company=${encodeURIComponent(name)}`;
}

function withCompanyContext(link, company, location) {
  if (!link || link === '#') return link;
  const hashIndex = link.indexOf('#');
  const hash = hashIndex >= 0 ? link.slice(hashIndex) : '';
  const beforeHash = hashIndex >= 0 ? link.slice(0, hashIndex) : link;
  const queryIndex = beforeHash.indexOf('?');
  const path = queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash;
  const query = queryIndex >= 0 ? beforeHash.slice(queryIndex) : '';
  if (query.includes('website=')) return `${path}${query}${hash}`;
  const search = companySearch(company, location);
  if (!search) return `${path}${query}${hash}`;
  const params = new URLSearchParams(query.replace(/^\?/, ''));
  const companyParams = new URLSearchParams(search.replace(/^\?/, ''));
  companyParams.forEach((value, key) => {
    if (!params.has(key)) params.set(key, value);
  });
  const nextQuery = params.toString();
  return `${path}${nextQuery ? `?${nextQuery}` : ''}${hash}`;
}

function ProgressRing({ value, total, accent = false }) {
  const radius = 9.75;
  const circumference = 2 * Math.PI * radius;
  const progress = total > 0 ? Math.min(value / total, 1) : 0;
  const offset = circumference * (1 - progress);
  const done = total > 0 && value >= total;

  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 22 22"
      className={`shrink-0 ${accent ? 'text-violet-600' : 'text-[#012241]'}`}
      aria-hidden="true"
    >
      <circle
        cx="11"
        cy="11"
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.18"
        strokeWidth="2.5"
      />
      <circle
        cx="11"
        cy="11"
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform="rotate(-90 11 11)"
        style={{ transition: 'stroke-dashoffset 500ms' }}
      />
      <path
        d="M 6.6 11.44 L 9.57 14.41 L 15.84 7.92"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          opacity: done ? 1 : 0,
          transformOrigin: 'center center',
          transform: done ? 'scale(1)' : 'scale(0.6)',
          transition:
            'opacity 350ms 250ms, transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1) 250ms',
        }}
      />
    </svg>
  );
}

function progressMessage(complete, total) {
  if (complete >= total) return "You're all set!";
  if (total - complete === 1) return 'One win left';
  if (complete <= 1) return "You're in. Pick your first win.";
  if (total - complete <= 3) return 'Almost there';
  return 'Keep the wins coming';
}

function isTaskChecked(index, profileComplete, checkedItems) {
  return index === 0 ? Boolean(profileComplete) : Boolean(checkedItems[index - 1]);
}

function navigateToTask(navigate, location, link) {
  if (!link || link === '#') return;
  if (link.startsWith('#')) {
    if (location.pathname !== '/') {
      navigate(`/${link}`);
      return;
    }
    window.location.hash = link;
    window.setTimeout(() => {
      const element = document.querySelector(link);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    return;
  }
  navigate(link);
}

const WIDE_SCREEN_QUERY = '(min-width: 1024px)';
const BOTTOM_REST = '1rem';
const BOTTOM_ABOVE_KEY_METRICS = '6rem';

function useWideScreen() {
  const [wide, setWide] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(WIDE_SCREEN_QUERY).matches : true
  );

  useEffect(() => {
    const media = window.matchMedia(WIDE_SCREEN_QUERY);
    const onChange = () => setWide(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return wide;
}

export default function OnboardingChecklistWidget({
  company,
  checkedItems = [],
  profileCompleteFromDB = false,
  sidebarCollapsed = true,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const steps = checklistSteps();
  const total = steps.length;
  const complete =
    1 + checkedItems.filter(Boolean).length + (profileCompleteFromDB ? 1 : 0);
  const freshStart = complete <= 1;
  const currentIndex = steps.findIndex((step, index) => {
    if (step.alwaysComplete) return false;
    return !isTaskChecked(index - 1, profileCompleteFromDB, checkedItems);
  });
  const wideScreen = useWideScreen();
  const liftForKeyMetrics =
    shouldShowKeyMetricsNav(location.pathname) && !wideScreen;
  const hidden = HIDDEN_PATHS.some((path) => location.pathname.startsWith(path));

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (hidden || dismissed) return null;

  const handleTaskClick = (taskConfig) => {
    if (taskConfig.alwaysComplete) return;
    const link = withCompanyContext(getTaskLink(taskConfig, company) || '#', company, location);
    setOpen(false);
    navigateToTask(navigate, location, link);
  };

  const handleReOnboard = () => {
    setOpen(false);
    const companyId = company?.id;
    if (companyId) {
      try {
        localStorage.setItem('onboardingV2_companyId', companyId);
      } catch {
        // Onboarding still reads the company id from the URL.
      }
      navigate(`/onboarding-v2?company=${encodeURIComponent(companyId)}`);
      return;
    }
    navigate('/onboarding-v2');
  };

  return (
    <div
      ref={rootRef}
      className={`fixed z-40 left-4 ${
        sidebarCollapsed ? 'lg:left-20' : 'lg:left-[17.5rem]'
      } transition-[bottom,left] duration-300 ease-out`}
      style={{
        bottom: liftForKeyMetrics ? BOTTOM_ABOVE_KEY_METRICS : BOTTOM_REST,
      }}
    >
      {open && (
        <div
          id="onboarding-checklist-panel"
          role="dialog"
          aria-labelledby="onboarding-checklist-title"
          className="absolute bottom-full left-0 mb-3 grid w-[22rem] max-w-[calc(100vw-2rem)] origin-bottom-left grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xl"
          style={{ maxHeight: 'min(36rem, calc(100dvh - 6.5rem))' }}
        >
          <div className="px-5 pt-4 pb-3">
            <div className="flex items-start justify-between gap-3">
              <h2
                id="onboarding-checklist-title"
                className="text-base font-semibold tracking-tight text-gray-900"
              >
                Getting started
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600"
                aria-label="Close getting started"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-3">
              <p className="text-sm text-gray-500">
                {complete}/{total} steps completed
              </p>
              <p
                className={`text-sm font-medium ${
                  freshStart ? 'text-violet-700' : 'text-gray-900'
                }`}
              >
                {progressMessage(complete, total)}
              </p>
            </div>
            <div className="mt-3 flex gap-1" aria-hidden="true">
              {steps.map((task, index) => (
                <div
                  key={`progress-${task.name}`}
                  className={`h-1.5 flex-1 rounded-full ${
                    index < complete
                      ? freshStart
                        ? 'bg-violet-500'
                        : 'bg-gray-900'
                      : freshStart
                        ? 'bg-violet-100'
                        : 'bg-gray-100'
                  }`}
                />
              ))}
            </div>
          </div>

          <ul className="min-h-0 overflow-y-auto overscroll-contain px-2 pb-1">
            {steps.map((taskConfig, index) => {
              const IconComponent = taskConfig.icon;
              const isChecked = taskConfig.alwaysComplete
                ? true
                : isTaskChecked(index - 1, profileCompleteFromDB, checkedItems);
              const isCurrent = index === currentIndex;

              return (
                <li key={`checklist-item-${index}`}>
                  <button
                    type="button"
                    onClick={() => handleTaskClick(taskConfig)}
                    className={`flex w-full items-center gap-3 rounded-xl px-2.5 py-1.5 text-left transition-colors duration-150 ${
                      isCurrent && freshStart
                        ? 'bg-violet-50'
                        : isCurrent
                          ? 'bg-gray-50'
                          : 'hover:bg-gray-50'
                    }`}
                    title={taskConfig.name}
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1 ${
                        isCurrent && freshStart
                          ? 'bg-violet-100 ring-violet-200'
                          : 'bg-gray-50 ring-gray-100'
                      }`}
                    >
                      <IconComponent
                        className={`h-4 w-4 ${
                          isCurrent && freshStart ? 'text-violet-700' : 'text-gray-600'
                        }`}
                      />
                    </span>
                    <span
                      className={`min-w-0 flex-1 truncate text-sm font-medium ${
                        isChecked ? 'text-gray-400' : 'text-gray-900'
                      }`}
                    >
                      {taskConfig.name}
                    </span>
                    {isChecked && (
                      <CheckCircleIcon
                        className="h-5 w-5 shrink-0 text-emerald-500"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="border-t border-gray-100 px-2 py-2">
            <button
              type="button"
              onClick={handleReOnboard}
              className="inline-flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Re-onboard from the start
            </button>
          </div>
        </div>
      )}

      <div className="relative">
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="absolute -left-1.5 -top-1.5 z-10 inline-flex h-5 w-5 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition-colors hover:text-gray-900"
        aria-label="Hide getting started until refresh"
      >
        <XMarkIcon className="h-3 w-3" />
      </button>
      <button
        type="button"
        data-tour="tracker"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="onboarding-checklist-panel"
        aria-label={`Getting started — ${complete} of ${total} steps complete`}
        className="gs-launcher inline-flex cursor-pointer items-center justify-center gap-2.5 rounded-2xl px-5 py-3.5 text-[15px] font-bold tracking-tight text-gray-900 transition hover:brightness-[1.03] active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
      >
        <ProgressRing value={complete} total={total} accent={freshStart} />
        Getting started
        <span
          className={`rounded-full px-2 py-0.5 text-[13px] font-extrabold tabular-nums ${
            freshStart
              ? 'bg-violet-100 text-violet-800'
              : 'bg-[#012241]/10 text-[#012241]'
          }`}
        >
          {complete}/{total}
        </span>
      </button>
      </div>
    </div>
  );
}
